import { FormEvent, useEffect, useState } from 'react';
import { portalApi } from '../lib/api';
import { currentMcpTarget } from '../lib/mcpEnv';

interface McpToken {
  id: string; name: string; prefix: string;
  createdAt: string; expiresAt: string; lastUsedAt: string | null; revokedAt: string | null;
}

const fmt = (s: string | null) => (s ? new Date(s).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : 'Never');

type Way = 'cli' | 'file' | 'curl';

function snippet(way: Way, url: string, token: string): string {
  if (way === 'cli') {
    // One line on purpose: a backslash continuation breaks in PowerShell/cmd and silently drops the header.
    return `claude mcp add --transport http wpos-admin ${url} --header "Authorization: Bearer ${token}"`;
  }
  if (way === 'file') {
    return JSON.stringify({ mcpServers: { 'wpos-admin': { type: 'http', url, headers: { Authorization: `Bearer ${token}` } } } }, null, 2);
  }
  return `curl.exe -s ${url} -H "content-type: application/json" -H "accept: application/json, text/event-stream" -H "authorization: Bearer ${token}" -d "{\\\"jsonrpc\\\":\\\"2.0\\\",\\\"id\\\":1,\\\"method\\\":\\\"tools/list\\\"}"`;
}

function CopyButton({ text, label = 'Copy', title, primary, disabled }: { text: string; label?: string; title?: string; primary?: boolean; disabled?: boolean }) {
  const [done, setDone] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1800); } catch { /* user can select it */ }
  }
  return <button type="button" className={'btn btn-sm' + (primary ? ' btn-primary' : '')} onClick={copy} title={title} disabled={disabled}>{done ? 'Copied' : label}</button>;
}

/**
 * One "Copy" menu per row: the token itself, or the complete setup command.
 * Without the remembered secret (token made elsewhere) the token is disabled
 * and the setup carries a placeholder.
 */
function RowCopy({ secret, url }: { secret?: string; url: string }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState('');
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  /* The list is position:fixed so the table's scroll area never clips it or grows a scrollbar. */
  function toggle(e: React.MouseEvent<HTMLButtonElement>) {
    const r = e.currentTarget.getBoundingClientRect();
    setPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
    setOpen((v) => !v);
  }
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [open]);
  async function copy(what: string, text: string) {
    try { await navigator.clipboard.writeText(text); setDone(what); setTimeout(() => setDone(''), 1600); } catch { /* user can select it */ }
    setOpen(false);
  }
  return (
    <span className="mcp-menu" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false); }}>
      <button type="button" className="btn btn-sm" aria-haspopup="menu" aria-expanded={open} onClick={toggle}>
        {done ? 'Copied' : 'Copy'} <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <span className="mcp-menu-list" role="menu" style={pos ? { top: pos.top, right: pos.right } : undefined}>
          <button type="button" role="menuitem" disabled={!secret} onClick={() => copy('token', secret!)}
            title={secret ? undefined : 'This token was created before this browser started remembering secrets, or on another device. Revoke it and create a new one to copy it.'}>Copy token</button>
          <button type="button" role="menuitem" onClick={() => copy('setup', snippet('cli', url, secret ?? PLACEHOLDER))}
            title={secret ? undefined : 'Copies the command with a wpm_YOUR_TOKEN placeholder'}>Copy setup</button>
        </span>
      )}
    </span>
  );
}

/** "3 hours ago", "in 12 days": a glance beats a timestamp for last-used and expiry. */
function relative(iso: string | null, empty = 'Never'): string {
  if (!iso) return empty;
  const diff = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(diff);
  const units: Array<[number, string]> = [[86_400_000, 'day'], [3_600_000, 'hour'], [60_000, 'minute']];
  for (const [ms, name] of units) {
    if (abs >= ms) { const n = Math.floor(abs / ms); return diff < 0 ? `${n} ${name}${n === 1 ? '' : 's'} ago` : `in ${n} ${name}${n === 1 ? '' : 's'}`; }
  }
  return 'just now';
}

const PLACEHOLDER = 'wpm_YOUR_TOKEN';

/**
 * The server keeps only a hash of each token, so a secret can never be read back
 * from it. To let you copy one later we remember the secrets of tokens created
 * in THIS browser (localStorage, this device only, same place the portal keeps
 * its own session). Tokens made elsewhere, or before this existed, have none.
 */
const SECRETS_KEY = 'wpos.mcp.secrets';
function readSecrets(): Record<string, string> {
  try { const v = JSON.parse(localStorage.getItem(SECRETS_KEY) || '{}'); return v && typeof v === 'object' ? v : {}; } catch { return {}; }
}
function writeSecrets(v: Record<string, string>) {
  try { localStorage.setItem(SECRETS_KEY, JSON.stringify(v)); } catch { /* storage blocked: copying later just is not offered */ }
}

const CAN = [
  'Overview, funnel, usage and growth',
  'Accounts, credits, conversations and transfers',
  'Agencies, members and their sites',
  'Sites, sandboxes and trials',
  'Support tickets, replies and audit trail',
  'Coupons, bookings, invites and emails',
  'Partners, releases and announcements',
  'Finances and AI prompts',
  'Staff users and roles',
];
const TRY = [
  'List the 5 newest support tickets',
  'Show ticket <id> and summarise the conversation',
  'Which sandboxes expire today?',
  'How many accounts signed up this week?',
];

export default function McpTokens() {
  const [tokens, setTokens] = useState<McpToken[]>([]);
  const [name, setName] = useState('');
  const [days, setDays] = useState(90);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fresh, setFresh] = useState<{ name: string; token: string } | null>(null);
  const [way, setWay] = useState<Way>('cli');
  const target = currentMcpTarget();
  const url = target.url;

  async function load() {
    try { setTokens(await portalApi.get<McpToken[]>('/mcp-tokens')); setError(''); }
    catch (e: any) { setError(e.message); }
  }
  const [secrets, setSecrets] = useState<Record<string, string>>(() => readSecrets());
  useEffect(() => { void load(); }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true); setError('');
    try {
      const r = await portalApi.post<{ id: string; name: string; token: string }>('/mcp-tokens', { name: name.trim(), ttlDays: days });
      const next = { ...readSecrets(), [r.id]: r.token };
      writeSecrets(next); setSecrets(next);
      setFresh({ name: r.name, token: r.token }); setName(''); setWay('cli');
      await load();
    } catch (err: any) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function revoke(t: McpToken) {
    if (!window.confirm(`Revoke "${t.name}"? Anything using it stops working within a few minutes.`)) return;
    try { await portalApi.delete(`/mcp-tokens/${encodeURIComponent(t.id)}`);
      const { [t.id]: _gone, ...rest } = readSecrets(); writeSecrets(rest); setSecrets(rest);
      await load(); }
    catch (err: any) { setError(err.message); }
  }

  const shown = fresh?.token ?? 'wpm_YOUR_TOKEN';
  const code = snippet(way, url, shown);

  return (
    <div className="mcp-page">
      <header className="mcp-hero">
        <div>
          <h1>MCP tokens</h1>
          <p>Ask Claude about portal data. A token sees only what your role can, and every call is logged against you.</p>
        </div>
        <span className="mcp-pill"><i /> Read-only</span>
      </header>
      {error && <div className="alert alert-error" role="alert">{error}</div>}

      {fresh && (
        <section className="card mcp-fresh" data-testid="fresh-token" aria-live="polite">
          <div className="mcp-fresh-head">
            <h2 className="card-title">New token “{fresh.name}”</h2>
            <button className="btn btn-sm btn-ghost" onClick={() => setFresh(null)}>Done</button>
          </div>
          <div className="mcp-token-row">
            <code className="mcp-token">{fresh.token}</code>
            <CopyButton text={fresh.token} label="Copy token" primary />
          </div>
        </section>
      )}

      <div className="mcp-grid">
        <div>
          <section className="card">
            <h2 className="mcp-step-title"><span className="mcp-step">1</span>Create a token</h2>
            <form className="mcp-form" onSubmit={create}>
              <label>Name
                <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="e.g. My laptop" required />
              </label>
              <label>Expires after
                <span className="mcp-days">
                  <input type="number" min={1} max={365} value={days} onChange={(e) => setDays(Math.min(365, Math.max(1, Number(e.target.value) || 90)))} />
                  <span className="muted">days</span>
                </span>
              </label>
              <button className="btn btn-primary" disabled={busy || !name.trim()}>{busy ? 'Creating…' : 'Create token'}</button>
            </form>
          </section>

          <section className="card">
            <h2 className="mcp-step-title"><span className="mcp-step">2</span>Connect Claude</h2>
            <div className="mcp-tabs" role="tablist" aria-label="How to connect">
              {([['cli', 'Claude Code'], ['file', '.mcp.json'], ['curl', 'Test with curl']] as Array<[Way, string]>).map(([k, label]) => (
                <button key={k} role="tab" aria-selected={way === k} className={'mcp-tab' + (way === k ? ' on' : '')} onClick={() => setWay(k)}>{label}</button>
              ))}
            </div>
            <div className="mcp-codebox">
              <div className="mcp-codebar">
                <span>{way === 'file' ? '.mcp.json' : 'Terminal'}</span>
                <CopyButton text={code} label="Copy setup" />
              </div>
              <pre className="mcp-code" data-testid="mcp-snippet">{code}</pre>
            </div>
            <p className="muted small">
              <span className={'mcp-env mcp-env-' + target.name} data-testid="mcp-env">{target.label}</span>{' '}
              {way === 'cli' && <>Run once in a terminal, then type <code>/mcp</code> in Claude Code to check it is connected.</>}
              {way === 'file' && <>Save as <code>.mcp.json</code> in your project and keep it out of git.</>}
              {way === 'curl' && <>Returns the tool list. A 401 means the token is wrong, expired or revoked.</>}
            </p>
          </section>

          <section className="card">
            <h2 className="mcp-step-title"><span className="mcp-step">3</span>Ask something</h2>
            <ul className="mcp-try">{TRY.map((t) => <li key={t}>{t}</li>)}</ul>
          </section>
        </div>

        <aside>
          <section className="card">
            <h2 className="card-title">What it can read</h2>
            <ul className="mcp-list mcp-yes">{CAN.map((t) => <li key={t}>{t}</li>)}</ul>
          </section>
        </aside>
      </div>

      <section className="card">
        <h2 className="card-title">Your tokens</h2>
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Name</th><th>Token</th><th>Created</th><th>Expires</th><th>Last used</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>
              {tokens.length === 0 && <tr><td colSpan={7} className="muted">No tokens yet. Create one above.</td></tr>}
              {tokens.map((t) => {
                const expired = new Date(t.expiresAt).getTime() < Date.now();
                const status = t.revokedAt ? 'Revoked' : expired ? 'Expired' : 'Active';
                return (
                  <tr key={t.id} style={status !== 'Active' ? { opacity: 0.6 } : undefined}>
                    <td>{t.name}</td>
                    <td><code>{t.prefix}…</code></td>
                    <td>{fmt(t.createdAt)}</td>
                    <td title={fmt(t.expiresAt)}>{relative(t.expiresAt)}</td>
                    <td title={t.lastUsedAt ? fmt(t.lastUsedAt) : undefined}>{relative(t.lastUsedAt)}</td>
                    <td><span className={'badge ' + (status === 'Active' ? 'badge-green' : 'badge-gray')}>{status}</span></td>
                    <td className="mcp-actions">
                      <div className="mcp-actions-inner">
                        {status === 'Active' && <RowCopy secret={secrets[t.id]} url={url} />}
                        {status === 'Active' && <button className="btn btn-sm btn-ghost mcp-revoke" onClick={() => revoke(t)} aria-label={`Revoke ${t.name}`}>Revoke</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="muted small mcp-note">Only tokens created in this browser can be copied. For any other, revoke it and create a new one.</p>
      </section>
    </div>
  );
}
